import {
  AppliedFilterInterface,
  FilterInterface,
  IndexFilters,
  IndexFiltersMode,
  useSetIndexFiltersMode,
} from '@shopify/polaris'

import { NonEmptyArray, useDialogActivator } from '../common/helpers'
import { TableColumnSelectorModal } from '../common/TableColumnSelectorModal'
import { SubscriptionFilter } from './api'
import { useAddAnchorsFilter } from './filters/Anchors'
import { useAddCurrencyFilter } from './filters/Currency'
import { useAddCustomerIdFilter } from './filters/CustomerId'
import { useAddDateFilter } from './filters/Date'
import { useAddDeliveryCountryCodeFilter } from './filters/DeliveryCountryCode'
import { useAddDeliveryMethodFilter } from './filters/DeliveryMethod'
import { useAddDunningRetriesFilter } from './filters/DunningRetries'
import { useAddFrequencyFilter } from './filters/Frequency'
import { useAddNotesFilter } from './filters/Notes'
import { useAddPrepaidDeliveriesFilter } from './filters/PrepaidDeliveries'
import { useAddPriceFilter } from './filters/Price'
import { useAddProductIdFilter } from './filters/Product'
import { useAddProductSkuFilter } from './filters/ProductSku'
import { useAddProductVariantIdFilter } from './filters/ProductVariant'
import { useSearchState } from './filters/Search'
import { useAddSellingPlanIdFilter } from './filters/SellingPlan'
import { useAddStatusFilter } from './filters/Status'
import { useAddSuccessfulPaymentsFilter } from './filters/SuccessfulPayments'
import {
  backendSortToIndex,
  indexSortToBackend,
  SubscriptionTableColumn,
  subscriptionTableColumnChoices,
  subscriptionTableIndexSortOptions,
} from './SubscriptionTable'

export const SubscriptionFilterForm = ({
  filter,
  updateFilter,
  clearFilter,
  columns,
  onColumnsChange,
}: {
  filter: SubscriptionFilter
  updateFilter: (newFilter: Partial<SubscriptionFilter>) => any
  clearFilter: () => any
  columns: NonEmptyArray<SubscriptionTableColumn>
  onColumnsChange: (columns: NonEmptyArray<SubscriptionTableColumn>) => any
}) => {
  const filters: FilterInterface[] = []
  const appliedFilters: AppliedFilterInterface[] = []

  const searchState = useSearchState(filter, updateFilter)
  useAddStatusFilter(filter, updateFilter, filters, appliedFilters)
  useAddDunningRetriesFilter(filter, updateFilter, filters, appliedFilters)
  useAddCustomerIdFilter(filter, updateFilter, filters, appliedFilters)
  useAddProductIdFilter(filter, updateFilter, filters, appliedFilters)
  useAddProductVariantIdFilter(filter, updateFilter, filters, appliedFilters)
  useAddProductSkuFilter(filter, updateFilter, filters, appliedFilters)
  useAddSellingPlanIdFilter(filter, updateFilter, filters, appliedFilters)
  useAddPriceFilter(filter, updateFilter, filters, appliedFilters)
  useAddCurrencyFilter(filter, updateFilter, filters, appliedFilters)
  useAddFrequencyFilter(filter, updateFilter, filters, appliedFilters)
  useAddPrepaidDeliveriesFilter(filter, updateFilter, filters, appliedFilters)
  useAddSuccessfulPaymentsFilter(filter, updateFilter, filters, appliedFilters)
  useAddAnchorsFilter(filter, updateFilter, filters, appliedFilters)
  useAddDateFilter('next_order', filter, updateFilter, filters, appliedFilters)
  useAddDateFilter('next_attempt', filter, updateFilter, filters, appliedFilters)
  useAddDateFilter('paused', filter, updateFilter, filters, appliedFilters)
  useAddDateFilter('finishing', filter, updateFilter, filters, appliedFilters)
  useAddDateFilter('finished', filter, updateFilter, filters, appliedFilters)
  useAddDeliveryMethodFilter(filter, updateFilter, filters, appliedFilters)
  useAddDeliveryCountryCodeFilter(filter, updateFilter, filters, appliedFilters)
  useAddDateFilter('created', filter, updateFilter, filters, appliedFilters)
  useAddDateFilter('updated', filter, updateFilter, filters, appliedFilters)
  useAddNotesFilter(filter, updateFilter, filters, appliedFilters)

  const { mode, setMode } = useSetIndexFiltersMode(
    appliedFilters.length > 0 || !!filter.search
      ? IndexFiltersMode.Filtering
      : IndexFiltersMode.Default
  )
  const columnsActivator = useDialogActivator()

  return (
    <>
      <IndexFilters
        mode={mode}
        setMode={setMode}
        filters={filters}
        appliedFilters={appliedFilters}
        queryValue={searchState.search ?? ''}
        queryPlaceholder="Search"
        onQueryChange={searchState.updateSearch}
        onQueryClear={searchState.clearSearch}
        onClearAll={clearFilter}
        sortOptions={subscriptionTableIndexSortOptions(!!filter.search)}
        sortSelected={[backendSortToIndex(!!filter.sort ? filter.sort : 'created_desc')]}
        onSort={(value) => value[0] && updateFilter({ sort: indexSortToBackend(value[0]) })}
        showEditColumnsButton
        onEditStart={(editMode) => {
          if (editMode === IndexFiltersMode.EditingColumns) {
            columnsActivator.show()
            setMode(IndexFiltersMode.Default)
          }
        }}
        cancelAction={{ onAction: () => {} }}
        tabs={[]}
        selected={0}
        onSelect={() => {}}
      />

      <TableColumnSelectorModal
        activator={columnsActivator}
        columns={columns}
        choices={subscriptionTableColumnChoices}
        onChange={onColumnsChange as (columns: NonEmptyArray<string>) => any}
      />
    </>
  )
}
